// Widget shell CSS + minimal styling for LiveKit component classes.
// The heavy lifting (ControlBar, BarVisualizer) is done by LiveKit's
// prefab components; we only style the widget container and messages.
//
// Per-card styles live next to their components (e.g. ./cards/booking/styles)
// and are concatenated below so we still inject one stylesheet at the
// shadow-root boundary.
import { APP_CSS } from "./App/App.styles";
import { AGENT_PANEL_CSS } from "./widget/AgentPanel/AgentPanel.styles";
import { AGENT_HEADER_CSS } from "./widget/AgentHeader/AgentHeader.styles";
import { MESSAGE_LIST_CSS } from "./widget/MessageList/MessageList.styles";
import { MESSAGE_INPUT_CSS } from "./widget/MessageInput/MessageInput.styles";
import { CALL_BUTTON_CSS } from "./widget/CallButton/CallButton.styles";
import { ERROR_BANNER_CSS, RECONNECT_STATUS_CSS } from "./widget/ErrorBanner/ErrorBanner.styles";
import { STARTER_CHIPS_CSS } from "./widget/StarterChips/StarterChips.styles";
import { AVATAR_VIDEO_CSS } from "./widget/AvatarVideo/AvatarVideo.styles";
import { CONVERSATION_FEEDBACK_CSS } from "./widget/ConversationFeedback/ConversationFeedback.styles";
import { BOOKING_CARD_CSS } from "./cards/booking/BookingCard.styles";
import { SCROLLABLE_CARDS_CSS } from "./cards/ScrollableCards.styles";
import { AVAILABILITY_CARD_CSS } from "./cards/availability/AvailabilityCard.styles";
import { PRODUCT_CARD_CSS } from "./cards/product/ProductCard.styles";
import { COACH_CARD_CSS } from "./cards/coach/CoachCard.styles";
import { TOURNAMENT_CARD_CSS } from "./cards/tournament/TournamentCard.styles";
import { CHECKOUT_CARD_CSS } from "./cards/checkout/CheckoutCard.styles";
import { CART_CARD_CSS } from "./cards/cart/CartCard.styles";
import { SKELETON_CSS } from "./cards/Skeleton.styles";

const SHELL_CSS = /* css */ `
  :host {
    --wg-accent: #0A4628;
    --wg-accent-dark: #0A3228;
    --wg-bg: #ffffff;
    --wg-bg-secondary: #EFF4F3;
    --wg-fg: #0A3228;
    --wg-fg-muted: #8C95A2;       /* Brand colors/Text/text-200 — caps labels */
    --wg-card-fg: #061934;        /* Foundation/Text/text-500 — card heading + body */
    --wg-card-fg-muted: #586577;  /* Brand colors/Text/text-300 — secondary card text */
    --wg-border: #e0e0e0;
    --wg-danger: #d32f2f;
    --wg-msg-bubble: #0A3228;
    --wg-msg-bubble-agent: #F8F8F1;
    --wg-avatar-glow: rgba(54, 209, 191, 0.85);
    --wg-image-placeholder-bg: #1a1a1a;
    --wg-slot-bg: #C3D2CE;        /* Foundation/Tertiary/tertiary-100 */
    --wg-cta-bg: #E1F700;
    --wg-cta-bg-hover: #cce000;
    --wg-send-active: #26D197;    /* Foundation/Secondary/Secondary_Teal */
    --wg-send-disabled: #B2B8C0;  /* Foundation/Text/text-100 */
    --wg-send-glow: #4fbd91;
    --wg-teal-glow: #36D1BF;      /* visualizer / avatar — distinct from send-active */
    --wg-clay: #E06437;           /* Brand colors/Secondary/Clay — call pill active */
    --wg-clay-light: #FDF1ED;     /* Brand colors/Secondary/Clay-10 */
    --wg-card-shadow:
      0 4px 4px -4px rgba(12, 12, 13, 0.05),
      0 16px 32px -4px rgba(12, 12, 13, 0.1);
    --wg-card-shadow-hover:
      0 8px 16px -4px rgba(12, 12, 13, 0.1),
      0 24px 48px -4px rgba(12, 12, 13, 0.15);

    /* ── Configurable offsets (separate desktop / mobile defaults) ── */
    --wg-desktop-offset-right: 20px;
    --wg-desktop-offset-bottom: 20px;
    --wg-mobile-offset-right: 20px;
    --wg-mobile-offset-bottom: 20px;

    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 14px; line-height: 1.4; color: var(--wg-fg);
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Visualizer ── */
  .lk-audio-bar-visualizer {
    display: flex; align-items: center; justify-content: center;
    gap: 3px; height: 100%; width: 100%;
  }
  .lk-audio-bar {
    width: 3px; border-radius: 1.5px; background: rgba(255,255,255,0.5);
    will-change: height;
  }
  .lk-audio-bar.lk-highlighted { background: var(--wg-teal-glow); }

  /* ── ControlBar (LiveKit prefab, shown inside header in voice mode) ── */
  .lk-control-bar {
    display: flex; align-items: center; gap: 6px;
  }
  .lk-button-group { display: flex; align-items: center; }
  .lk-button-group-menu { display: none; }
  .lk-button {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 4px 8px; border-radius: 50%;
    border: none; background: rgba(255,255,255,0.15);
    color: white; font-family: inherit; font-size: 0;
    cursor: pointer; white-space: nowrap;
  }
  .lk-button:hover { background: rgba(255,255,255,0.25); }
  .lk-button svg { width: 16px; height: 16px; flex-shrink: 0; }
  .lk-button[aria-pressed="true"] {
    background: rgba(54, 209, 191, 0.5); color: white;
  }
  /* StartAudio (LiveKit prefab) — only visible when the browser blocks audio
     playback. Position above the trigger so it doesn't fight the chat panel. */
  .lk-start-audio-button {
    position: fixed;
    bottom: calc(var(--wg-desktop-offset-bottom) + 76px);
    right: var(--wg-desktop-offset-right);
    z-index: 2147483647;
    padding: 10px 16px; border-radius: 24px; border: none;
    background: var(--wg-accent); color: white;
    font-family: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.18);
  }
  .lk-start-audio-button:hover { opacity: 0.9; }

  @keyframes lk-card-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes lk-card-out {
    from { opacity: 1; transform: translateY(0); }
    to   { opacity: 0; transform: translateY(-8px) scale(0.97); }
  }
  .lk-widget-msg--card-exiting {
    animation: lk-card-out 0.3s ease forwards;
    pointer-events: none;
  }
`;

export const WIDGET_CSS =
  SHELL_CSS + APP_CSS + AGENT_PANEL_CSS + AGENT_HEADER_CSS +
  MESSAGE_LIST_CSS + MESSAGE_INPUT_CSS + CALL_BUTTON_CSS + ERROR_BANNER_CSS + RECONNECT_STATUS_CSS +
  STARTER_CHIPS_CSS + AVATAR_VIDEO_CSS + CONVERSATION_FEEDBACK_CSS +
  BOOKING_CARD_CSS + AVAILABILITY_CARD_CSS + SCROLLABLE_CARDS_CSS +
  PRODUCT_CARD_CSS + COACH_CARD_CSS + TOURNAMENT_CARD_CSS + CHECKOUT_CARD_CSS + CART_CARD_CSS + SKELETON_CSS;
